import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CreditCard, Shield, Clock, Check, Calendar, Users, MapPin } from "lucide-react";
import { calcMonthly } from "@/lib/providers/index";
import type { Plan } from "@/lib/finance-plans";

// ─── Vertical config ───────────────────────────────────────────────

const VERTICAL_CONFIG = {
  travel: {
    confirmTitle: "Confirm Your Booking",
    confirmSubtitle: "Please review your booking details before proceeding to payment.",
    ctaLabel: (provider: string) => `Confirm & Pay with ${provider}`,
    disclaimer:
      "By confirming this booking, you agree to our Terms and Conditions and the selected BNPL provider's terms.",
    redirectNote: (provider: string) =>
      `You will be redirected to ${provider} to complete your payment setup and approval process.`,
  },
  education: {
    confirmTitle: "Confirm Your Payment",
    confirmSubtitle: "Review your details before proceeding",
    ctaLabel: (_provider?: string) => "Confirm Payment & Enroll",
    disclaimer:
      "This enrollment confirmation is subject to school approval and available capacity. You will receive a confirmation email with next steps within 24 hours. Please ensure you have the school invoice ready for upload.",
    redirectNote: () => "",
  },
  healthcare: {
    confirmTitle: "Confirm Treatment Booking",
    confirmSubtitle: "",
    ctaLabel: (_provider?: string) => "Confirm & Proceed to Payment",
    disclaimer:
      "This is an estimate. Final billing may vary based on actual treatment requirements and facility charges. You will be redirected to your selected payment provider to complete the financing application.",
    redirectNote: () => "",
  },
} as const;

// ─── Per-vertical props (discriminated union) ───────────────────────

interface TravelBookingData {
  packageId: string;
  startDate: string;
  endDate: string;
  travelers: number;
  rooms: number;
  selectedProvider?: string;
  specialRequests?: string;
}

interface TravelPaymentProps {
  vertical: "travel";
  bookingData: TravelBookingData;
  onConfirm: () => void;
}

interface EducationPayerDetails {
  fullName: string;
  email: string;
  phone: string;
}

interface EducationPaymentProps {
  vertical: "education";
  amountAED: number;
  financingPlan: Plan;
  payerDetails: EducationPayerDetails;
  onBack: () => void;
  onConfirm: () => void;
}

interface HealthcarePaymentProps {
  vertical: "healthcare";
  bookingData: {
    treatmentId: string;
    treatment: any;
    preferredDate: string;
    facility: string;
    patientName: string;
    patientPhone: string;
    patientEmail: string;
    emergencyContact: string;
    emergencyPhone: string;
    provider?: string;
    medicalHistory?: string;
  };
  onConfirm: () => void;
}

export type PaymentConfirmationProps =
  | TravelPaymentProps
  | EducationPaymentProps
  | HealthcarePaymentProps;

// ─── Travel sub-component ───────────────────────────────────────────

function TravelConfirmation({ bookingData, onConfirm }: TravelPaymentProps) {
  const cfg = VERTICAL_CONFIG.travel;
  const packagePrice = 4200;
  const monthlyPayment = calcMonthly(packagePrice, 12);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-AE", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const calculateNights = () => {
    const start = new Date(bookingData.startDate);
    const end = new Date(bookingData.endDate);
    return Math.ceil(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  };

  const providerName =
    bookingData.selectedProvider === "tabby"
      ? "Tabby"
      : bookingData.selectedProvider === "tamara"
        ? "Tamara"
        : bookingData.selectedProvider === "cashew"
          ? "Cashew"
          : "BNPL Provider";

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6 text-foreground">{cfg.confirmTitle}</h2>
      <p className="text-muted-foreground mb-8">{cfg.confirmSubtitle}</p>

      <div className="space-y-6 mb-8">
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Package Details
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Package ID</span>
              <span className="text-foreground font-medium">{bookingData.packageId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Destination</span>
              <span className="text-foreground font-medium">Selected Package</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Duration</span>
              <span className="text-foreground font-medium">{calculateNights()} nights</span>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Travel Details
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Departure</span>
              <span className="text-foreground font-medium">{formatDate(bookingData.startDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Return</span>
              <span className="text-foreground font-medium">{formatDate(bookingData.endDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Travelers</span>
              <span className="text-foreground font-medium">{bookingData.travelers} Adults</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Rooms</span>
              <span className="text-foreground font-medium">
                {bookingData.rooms} Room{bookingData.rooms > 1 ? "s" : ""}
              </span>
            </div>
            {bookingData.specialRequests && (
              <div className="pt-2 border-t border-border">
                <span className="text-muted-foreground block mb-1">Special Requests</span>
                <span className="text-foreground text-sm">{bookingData.specialRequests}</span>
              </div>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Payment Method
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Provider</span>
              <span className="text-foreground font-medium">{providerName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Payment Plan</span>
              <span className="text-foreground font-medium">12 Monthly Installments</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Monthly Payment</span>
              <span className="text-foreground font-medium">AED {monthlyPayment}</span>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-primary/5 border-primary/20">
          <h3 className="text-lg font-semibold text-foreground mb-4">Price Summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Package Total</span>
              <span className="text-foreground">AED {packagePrice.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Processing Fee</span>
              <span className="text-foreground">Included</span>
            </div>
            <div className="border-t border-border pt-3">
              <div className="flex justify-between text-lg font-semibold">
                <span className="text-foreground">Total Amount</span>
                <span className="text-primary">AED {packagePrice.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="bg-muted/50 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-muted-foreground">
            <p className="mb-2">{cfg.disclaimer}</p>
            <p>{cfg.redirectNote(providerName)}</p>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={onConfirm} size="lg" className="px-8 py-3">
          {cfg.ctaLabel(providerName)}
        </Button>
      </div>
    </div>
  );
}

// ─── Education sub-component ────────────────────────────────────────

function EducationConfirmation({ amountAED, financingPlan, payerDetails, onBack, onConfirm }: EducationPaymentProps) {
  const cfg = VERTICAL_CONFIG.education;
  const processingFee = Math.round(amountAED * 0.025);
  const totalAmount = amountAED + processingFee;

  return (
    <Card className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <div>
          <h3 className="text-xl font-semibold">{cfg.confirmTitle}</h3>
          <p className="text-sm text-muted-foreground">{cfg.confirmSubtitle}</p>
        </div>
      </div>

      <div className="space-y-6">
        <Card className="p-4">
          <h4 className="font-medium mb-4 flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Payment Summary
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Course Fee:</span>
              <span>AED {amountAED.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Processing Fee:</span>
              <span>AED {processingFee.toLocaleString()}</span>
            </div>
            <Separator />
            <div className="flex justify-between font-semibold text-base">
              <span>Total Amount:</span>
              <span>AED {totalAmount.toLocaleString()}</span>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h4 className="font-medium mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Selected Financing Plan
          </h4>
          <div className="space-y-3">
            <div>
              <div className="font-medium">{financingPlan.label}</div>
              {financingPlan.notes && financingPlan.notes.length > 0 && (
                <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                  {financingPlan.notes.map((note, idx) => (
                    <li key={idx}>• {note}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              {financingPlan.breakdown.bnplAED && (
                <div className="bg-blue-50 p-3 rounded">
                  <div className="font-medium text-blue-900">BNPL</div>
                  <div className="text-blue-700">AED {financingPlan.breakdown.bnplAED.toLocaleString()}</div>
                </div>
              )}
              {financingPlan.breakdown.bankFinanceAED && (
                <div className="bg-green-50 p-3 rounded">
                  <div className="font-medium text-green-900">Bank Finance</div>
                  <div className="text-green-700">AED {financingPlan.breakdown.bankFinanceAED.toLocaleString()}</div>
                </div>
              )}
              {financingPlan.breakdown.upfrontAED && (
                <div className="bg-gray-50 p-3 rounded">
                  <div className="font-medium text-gray-900">Upfront</div>
                  <div className="text-gray-700">AED {financingPlan.breakdown.upfrontAED.toLocaleString()}</div>
                </div>
              )}
            </div>

            {financingPlan.tenureMonths && (
              <div className="text-sm text-muted-foreground">
                Tenure: {financingPlan.tenureMonths} months
                {financingPlan.aprPercent && ` • APR: ${financingPlan.aprPercent}%`}
              </div>
            )}
          </div>
        </Card>

        <Card className="p-4">
          <h4 className="font-medium mb-4">Guardian Information</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Name:</span>
              <span className="font-medium">{payerDetails.fullName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email:</span>
              <span>{payerDetails.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Phone:</span>
              <span>{payerDetails.phone}</span>
            </div>
          </div>
        </Card>

        <div className="bg-muted p-4 rounded-md">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="text-sm font-medium mb-1">Important Notice</p>
              <p className="text-xs text-muted-foreground">{cfg.disclaimer}</p>
            </div>
          </div>
        </div>

        <Button onClick={onConfirm} className="w-full" size="lg">
          {cfg.ctaLabel("")}
        </Button>
      </div>
    </Card>
  );
}

// ─── Healthcare sub-component ───────────────────────────────────────

function HealthcareConfirmation({ bookingData, onConfirm }: HealthcarePaymentProps) {
  const cfg = VERTICAL_CONFIG.healthcare;
  const { treatment } = bookingData;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{cfg.confirmTitle}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="font-semibold mb-3">Treatment Details</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Treatment:</span>
                <span className="font-medium">{treatment.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Category:</span>
                <Badge variant="secondary">{treatment.category}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Duration:</span>
                <span>{treatment.duration}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Estimated Cost:</span>
                <span className="font-bold text-lg">AED {treatment.price.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <h3 className="font-semibold mb-3">Booking Information</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Preferred Date:</span>
                <span>{new Date(bookingData.preferredDate).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Facility:</span>
                <span>{bookingData.facility}</span>
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <h3 className="font-semibold mb-3">Patient Information</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Patient Name:</span>
                <span>{bookingData.patientName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email:</span>
                <span>{bookingData.patientEmail}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Phone:</span>
                <span>{bookingData.patientPhone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Emergency Contact:</span>
                <span>{bookingData.emergencyContact}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Emergency Phone:</span>
                <span>{bookingData.emergencyPhone}</span>
              </div>
            </div>
          </div>

          {bookingData.medicalHistory && (
            <>
              <Separator />
              <div>
                <h3 className="font-semibold mb-3">Medical History / Notes</h3>
                <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                  {bookingData.medicalHistory}
                </p>
              </div>
            </>
          )}

          <Separator />

          <div>
            <h3 className="font-semibold mb-3">Payment Method</h3>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Selected Provider:</span>
              <Badge variant="outline" className="capitalize">
                {bookingData.provider || "Not selected"}
              </Badge>
            </div>
          </div>

          <div className="bg-muted p-4 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> {cfg.disclaimer}
            </p>
          </div>

          <Button onClick={onConfirm} className="w-full" size="lg">
            {cfg.ctaLabel("")}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Main component (dispatcher) ────────────────────────────────────

export default function PaymentConfirmation(props: PaymentConfirmationProps) {
  switch (props.vertical) {
    case "travel":
      return <TravelConfirmation {...props} />;
    case "education":
      return <EducationConfirmation {...props} />;
    case "healthcare":
      return <HealthcareConfirmation {...props} />;
  }
}
